import React, { useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';

import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

import { getDateReservations, selectDateReservations, selectIsLoadingDateReservations, selectProduct } from '../../common/store/productSlice';
import { selectShowReservationsOnHoverInAvailabilityCalendar } from '../../common/store/settingsSlice';

const PopoverContainer = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 1000,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    width: 'auto',
    minWidth: '700px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
}));

const DateInfoPopover = ({ day, position, isVisible, segments = [] }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const product = useSelector(selectProduct);
    const reservations = useSelector(selectDateReservations);
    const isLoading = useSelector(selectIsLoadingDateReservations);
    const showReservations = useSelector(selectShowReservationsOnHoverInAvailabilityCalendar);

    useEffect(() => {
        if (isVisible && day && product && showReservations) {
            dispatch(getDateReservations({ date: day, product }));
        }
    }, [isVisible, day, product, showReservations, dispatch]);

    if (!isVisible) return null;

    return (
        <PopoverContainer
            style={{
                left: position?.x || 0,
                top: position?.y || 0
            }}
        >
            {segments.length > 0 && (
                <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ paddingLeft: '16px' }}>
                        {t("popups.availability_calendar.segments_title")}
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("popups.availability_calendar.columns.von")}</TableCell>
                                    <TableCell>{t("popups.availability_calendar.columns.bis")}</TableCell>
                                    <TableCell>{t("popups.availability_calendar.segments.available")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {segments.map((seg, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{seg.from}</TableCell>
                                        <TableCell>{seg.to}</TableCell>
                                        <TableCell>{seg.stock_left}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {showReservations && (
            <>
            <Typography variant="h6" gutterBottom sx={{ paddingLeft: '16px' }}>
                {t("popups.availability_calendar.title")}
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("popups.availability_calendar.columns.nr")}</TableCell>
                            <TableCell>{t("popups.availability_calendar.columns.status")}</TableCell>
                            <TableCell>{t("popups.availability_calendar.columns.anzahl")}</TableCell>
                            <TableCell>{t("popups.availability_calendar.columns.person")}</TableCell>
                            <TableCell>{t("popups.availability_calendar.columns.von")}</TableCell>
                            <TableCell>{t("popups.availability_calendar.columns.bis")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : reservations.length > 0 ? (
                            reservations.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{item.reservationNr}</TableCell>
                                    <TableCell>{item.contractType}</TableCell>
                                    <TableCell>{item.quantity}</TableCell>
                                    <TableCell>{item.customer}</TableCell>
                                    <TableCell>{item.startDate}</TableCell>
                                    <TableCell>{item.endDate}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {t("popups.availability_calendar.no_reservations")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </>
            )}
        </PopoverContainer>
    );
};

export default DateInfoPopover;
